import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';

type TemporaryAccessUser = {
  id: number;
  login_username?: string | null;
  firstName?: string;
  label?: string;
  isActive: boolean;
  is_temporary_access?: boolean;
  access_expires_at?: string | null;
  createdAt: string;
};

interface ProviderTemporaryAccessManagerProps {
  providerId: number;
  providerName?: string;
}

const EVENT_ACCESS_LAST_NAMES = new Set(['Accès temporaire', 'Accès événement']);

const USERNAME_RE = /^[a-zA-Z0-9._-]{3,64}$/;

const ProviderTemporaryAccessManager: React.FC<ProviderTemporaryAccessManagerProps> = ({
  providerId,
  providerName,
}) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [credentials, setCredentials] = useState<{ login_username: string; password: string } | null>(null);
  const [form, setForm] = useState({
    login_username: '',
    label: '',
    password: '',
    expires_in_days: '7',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['provider-users', { providerId }],
    queryFn: () => apiService.getProviderUsers({ providerId }),
    enabled: !!providerId,
    select: (response) =>
      ((response.data.users || []) as TemporaryAccessUser[]).filter((u) => u.is_temporary_access),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiService.createProviderUser({
        provider_id: providerId,
        login_username: form.login_username.trim(),
        label: form.label.trim() || form.login_username.trim(),
        password: form.password.trim() || undefined,
        expires_in_days: form.expires_in_days === '' ? undefined : Number(form.expires_in_days),
        send_access_email: false,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      if (response.data?.credentials) {
        setCredentials(response.data.credentials);
      }
      setForm({ login_username: '', label: '', password: '', expires_in_days: '7' });
      setFormError(null);
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error || 'Erreur lors de la création');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: number; isActive: boolean }) =>
      isActive ? apiService.deactivateProviderUser(userId) : apiService.activateProviderUser(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-users'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: number) => apiService.deleteProviderUser(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-users'] }),
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const username = form.login_username.trim();
    if (!USERNAME_RE.test(username)) {
      setFormError('Identifiant : 3 à 64 caractères (lettres, chiffres, . _ -).');
      return;
    }
    if (form.password && form.password.length < 5) {
      setFormError('Le mot de passe doit contenir au moins 5 caractères.');
      return;
    }
    createMutation.mutate();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCredentials(null);
    setFormError(null);
  };

  const isExpired = (accessExpiresAt?: string | null) => {
    if (!accessExpiresAt) return false;
    return new Date(accessExpiresAt).getTime() <= Date.now();
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Accès événement dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comptes identifiant + mot de passe pour les équipes sur le terrain
              {providerName ? ` (${providerName})` : ''}. Connexion sans email sur Sherlook Pro.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Créer un accès
          </Button>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : users.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aucun accès événement. Idéal pour festivals et événements (accueil, stands, bénévoles…).
          </Typography>
        ) : (
          <List dense disablePadding>
            {users.map((user) => (
              <ListItem key={user.id} divider sx={{ px: 0 }}>
                <KeyIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="subtitle1" fontFamily="monospace" component="span">
                        {user.login_username}
                      </Typography>
                      {!user.isActive ? (
                        <Chip label="Désactivé" size="small" />
                      ) : isExpired(user.access_expires_at) ? (
                        <Chip label="Expiré" size="small" color="warning" />
                      ) : (
                        <Chip label="Actif" size="small" color="success" />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      {user.firstName &&
                        !EVENT_ACCESS_LAST_NAMES.has(String(user.firstName)) &&
                        user.firstName !== user.login_username && (
                        <Typography variant="body2" color="text.secondary" component="span" display="block">
                          {user.firstName}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" component="span">
                        {user.access_expires_at
                          ? `Expire le ${new Date(user.access_expires_at).toLocaleDateString('fr-FR')}`
                          : 'Sans date d’expiration'}
                        {' · '}
                        Créé le {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                      </Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Button
                    size="small"
                    onClick={() => toggleMutation.mutate({ userId: user.id, isActive: user.isActive })}
                  >
                    {user.isActive ? 'Désactiver' : 'Activer'}
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      if (window.confirm(`Supprimer l’accès « ${user.login_username} » ?`)) {
                        deleteMutation.mutate(user.id);
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvel accès événement</DialogTitle>
        {credentials ? (
          <>
            <DialogContent>
              <Alert severity="success" sx={{ mb: 2 }}>
                Communiquez ces identifiants à l’équipe. Le mot de passe ne sera plus affiché ensuite.
              </Alert>
              <Box sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <div>Identifiant : {credentials.login_username}</div>
                <div>Mot de passe : {credentials.password}</div>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={handleCloseDialog}>
                Fermer
              </Button>
            </DialogActions>
          </>
        ) : (
          <form onSubmit={handleCreate}>
            <DialogContent>
              {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formError}
                </Alert>
              )}
              <TextField
                fullWidth
                required
                label="Identifiant de connexion"
                value={form.login_username}
                onChange={(e) => setForm((p) => ({ ...p, login_username: e.target.value }))}
                helperText="Ex. Festival2026-ACCUEIL (sensible à la casse à la connexion)"
                margin="normal"
              />
              <TextField
                fullWidth
                label="Libellé (optionnel)"
                value={form.label}
                onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                helperText="Poste, stand, équipe…"
                margin="normal"
              />
              <TextField
                fullWidth
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                helperText="Laisser vide pour générer automatiquement"
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Durée (jours)"
                type="number"
                value={form.expires_in_days}
                onChange={(e) => setForm((p) => ({ ...p, expires_in_days: e.target.value }))}
                helperText="Vide = sans expiration"
                margin="normal"
                inputProps={{ min: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Annuler</Button>
              <Button type="submit" variant="contained" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Création…' : 'Créer'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Dialog>
    </Card>
  );
};

export default ProviderTemporaryAccessManager;
